// src/features/planning/components/RevisionStatusBadge.tsx
// ADR-092 Rule PLN-FE-03: MPS has only ONE status axis (unlike Quality's
// status + approval_state pair in ADR-091) — do not copy the two-axis
// combination logic from features/quality here, it doesn't apply.

import type { RevisionLifecycleStatus } from "../types";

export interface RevisionStatusLabel {
  label: string;
  colorToken: "gray" | "green" | "blue" | "darkGray";
}

/**
 * Pure function — exported and unit-testable independently of the React
 * component, matching the pattern already established in
 * features/quality/components/RevisionStatusBadge.tsx.
 */
export function resolveRevisionStatusLabel(
  status: RevisionLifecycleStatus
): RevisionStatusLabel {
  switch (status) {
    case "draft":
      return { label: "پیش‌نویس", colorToken: "gray" };
    case "published":
      return { label: "منتشرشده", colorToken: "green" };
    case "superseded":
      return { label: "جایگزین‌شده", colorToken: "blue" };
    case "archived":
      return { label: "آرشیوشده", colorToken: "darkGray" };
    default:
      // eslint-disable-next-line no-console
      console.warn(`[planning] unexpected revision status: "${status}"`);
      return { label: "وضعیت نامشخص", colorToken: "gray" };
  }
}

const COLOR_STYLES: Record<
  RevisionStatusLabel["colorToken"],
  { background: string; color: string }
> = {
  gray: { background: "rgba(148,163,184,0.15)", color: "#94a3b8" },
  green: { background: "rgba(34,197,94,0.15)", color: "#22c55e" },
  blue: { background: "rgba(96,165,250,0.15)", color: "#60a5fa" },
  darkGray: { background: "rgba(71,85,105,0.25)", color: "#64748b" },
};

export interface RevisionStatusBadgeProps {
  status: RevisionLifecycleStatus;
}

export function RevisionStatusBadge({ status }: RevisionStatusBadgeProps) {
  const { label, colorToken } = resolveRevisionStatusLabel(status);
  const { background, color } = COLOR_STYLES[colorToken];

  return (
    <span
      data-testid="mps-revision-status-badge"
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background,
        color,
      }}
    >
      {label}
    </span>
  );
}
